import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { ConfirmDialogModal } from '../shared/common/ConfirmDialogModal.tsx';

/** Shown when leaving /materiality with uploads (sidebar, logout, tab close, F5 / Ctrl+R). */
export const MATERIALITY_LEAVE_CONFIRM_MESSAGE =
  'You may lose all unsaved work on this assessment. Are you sure you want to leave?' as const;

export type ConfirmLeaveOptions = {
  onStay?: () => void;
};

export type ConfirmLeaveFn = (
  onConfirmLeave: () => void,
  options?: ConfirmLeaveOptions
) => void;

export type MaterialityLeaveGuardContextValue = {
  hasUnsavedProgress: boolean;
  setMaterialityHasUnsavedProgress: (value: boolean) => void;
  confirmLeaveIfNeeded: ConfirmLeaveFn;
};

function useLeaveGuardModalController(hasUnsavedProgress: boolean) {
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);

  const leaveModalOpenRef = useRef(false);
  const skipBeforeUnloadRef = useRef(false);
  const pendingOnLeaveRef = useRef<(() => void) | null>(null);
  const pendingOnStayRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    leaveModalOpenRef.current = leaveModalOpen;
  }, [leaveModalOpen]);

  const clearPendingLeave = useCallback(() => {
    pendingOnLeaveRef.current = null;
    pendingOnStayRef.current = null;
    setLeaveModalOpen(false);
  }, []);

  const confirmLeaveIfNeeded = useCallback<ConfirmLeaveFn>(
    (onConfirmLeave, options = {}) => {
      if (!hasUnsavedProgress) {
        onConfirmLeave();
        return;
      }
      pendingOnLeaveRef.current = onConfirmLeave;
      pendingOnStayRef.current = typeof options.onStay === 'function' ? options.onStay : null;
      setLeaveModalOpen(true);
    },
    [hasUnsavedProgress]
  );

  const handleModalStay = useCallback(() => {
    const onStay = pendingOnStayRef.current;
    clearPendingLeave();
    onStay?.();
  }, [clearPendingLeave]);

  const handleModalLeave = useCallback(() => {
    const fn = pendingOnLeaveRef.current;
    clearPendingLeave();
    fn?.();
  }, [clearPendingLeave]);

  return {
    leaveModalOpen,
    leaveModalOpenRef,
    skipBeforeUnloadRef,
    confirmLeaveIfNeeded,
    handleModalStay,
    handleModalLeave,
  };
}

function useLeaveGuardBeforeUnload(
  hasUnsavedProgress: boolean,
  message: string,
  skipRef: MutableRefObject<boolean>
): void {
  useEffect(() => {
    if (typeof window === 'undefined' || !hasUnsavedProgress) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (skipRef.current) return;
      event.preventDefault();
      event.returnValue = message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedProgress, message, skipRef]);
}

function useLeaveGuardReloadShortcut(
  hasUnsavedProgress: boolean,
  leaveModalOpenRef: MutableRefObject<boolean>,
  confirmLeaveIfNeeded: ConfirmLeaveFn,
  skipBeforeUnloadRef: MutableRefObject<boolean>
): void {
  useEffect(() => {
    if (typeof window === 'undefined' || !hasUnsavedProgress) return undefined;

    const onKeyDown = (e: KeyboardEvent) => {
      const isReload =
        e.key === 'F5' || ((e.ctrlKey || e.metaKey) && (e.key === 'r' || e.key === 'R'));
      if (!isReload) return;

      if (leaveModalOpenRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      confirmLeaveIfNeeded(() => {
        skipBeforeUnloadRef.current = true;
        window.location.reload();
      });
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [hasUnsavedProgress, confirmLeaveIfNeeded, leaveModalOpenRef, skipBeforeUnloadRef]);
}

const MaterialityLeaveGuardContext =
  createContext<MaterialityLeaveGuardContextValue | null>(null);

type MaterialityLeaveGuardProviderProps = {
  children: ReactNode;
};

export function MaterialityLeaveGuardProvider({ children }: MaterialityLeaveGuardProviderProps) {
  const [hasUnsavedProgress, setHasUnsavedProgressState] = useState(false);

  const setMaterialityHasUnsavedProgress = useCallback((value: boolean) => {
    setHasUnsavedProgressState(!!value);
  }, []);

  const {
    leaveModalOpen,
    leaveModalOpenRef,
    skipBeforeUnloadRef,
    confirmLeaveIfNeeded,
    handleModalStay,
    handleModalLeave,
  } = useLeaveGuardModalController(hasUnsavedProgress);

  useLeaveGuardBeforeUnload(
    hasUnsavedProgress,
    MATERIALITY_LEAVE_CONFIRM_MESSAGE,
    skipBeforeUnloadRef
  );
  useLeaveGuardReloadShortcut(
    hasUnsavedProgress,
    leaveModalOpenRef,
    confirmLeaveIfNeeded,
    skipBeforeUnloadRef
  );

  const contextValue = useMemo<MaterialityLeaveGuardContextValue>(
    () => ({
      hasUnsavedProgress,
      setMaterialityHasUnsavedProgress,
      confirmLeaveIfNeeded,
    }),
    [hasUnsavedProgress, setMaterialityHasUnsavedProgress, confirmLeaveIfNeeded]
  );

  return (
    <MaterialityLeaveGuardContext.Provider value={contextValue}>
      {children}
      <ConfirmDialogModal
        isOpen={leaveModalOpen}
        onClose={handleModalStay}
        title="Unsaved changes"
        subtitle="Leaving this page"
        message={MATERIALITY_LEAVE_CONFIRM_MESSAGE}
        variant="danger"
        cancelLabel="Stay on page"
        onCancel={handleModalStay}
        confirmLabel="Leave anyway"
        onConfirm={handleModalLeave}
      />
    </MaterialityLeaveGuardContext.Provider>
  );
}

const defaultContextValue: MaterialityLeaveGuardContextValue = {
  hasUnsavedProgress: false,
  setMaterialityHasUnsavedProgress: () => {},
  confirmLeaveIfNeeded: (onConfirmLeave) => {
    onConfirmLeave();
  },
};

export function useMaterialityLeaveGuard(): MaterialityLeaveGuardContextValue {
  const ctx = useContext(MaterialityLeaveGuardContext);
  return ctx ?? defaultContextValue;
}

/**
 * Stable click handler for “Sign out” when the leave guard may need to confirm first.
 */
export function useMaterialitySignOutWithLeaveGuard(
  confirmLeaveIfNeeded: ConfirmLeaveFn,
  needsLeaveConfirmation: boolean,
  onSignOut: () => void,
  closeDropdown: () => void
): () => void {
  return useCallback(() => {
    if (needsLeaveConfirmation) {
      confirmLeaveIfNeeded(
        () => {
          onSignOut();
          closeDropdown();
        },
        { onStay: closeDropdown }
      );
      return;
    }
    onSignOut();
    closeDropdown();
  }, [needsLeaveConfirmation, confirmLeaveIfNeeded, onSignOut, closeDropdown]);
}
